import React, { useState } from "react"
import { Checkbox, IconButton, Tooltip } from "@mui/material"
import { useDispatch } from "react-redux"
import trashIcon from "../../assets/photo/trash.svg"
import phyalbumIcon from "../../assets/photo/physical-album.svg"
import phyalbumGoldIcon from "../../assets/photo/gold-physical-album.svg"
import downloadIcon from "../../assets/photo/download.svg"
import shareIcon from "../../assets/photo/share.svg"
import moveIcon from "../../assets/photo/move.svg"
import playIcon from "../../assets/photo/Play Button.svg"
import playIconDark from "../../assets/black/Play Button.svg"
import { MyDispatch, MySelector } from "../../redux/store"
import useLazyLoad from "../../utils/useLazyLoad"
import { checkVideo } from "../../utils/isVideo"
import { HandleAsyncFns } from "../../hooks/handleAsyncFns"
import {
  setDeleteArray,
  setFullScreen,
  setFullScreenObject,
  setMoveFolderArray,
  setSelectedPhotos,
} from "../../redux/utilSlice"
import { checkInPhysicalAlbum } from "../../utils/checkInPhysicalAlbum"
import { getFilesInAnyAlbum } from "../../utils/getFilesInAnyAlbum"
import WatermarkOverlay from "../utils/WatermarkOverlay"
import { getOptimalUrl, getOptimalUrls, MediaFile } from "../../utils/getOptimalUrl"

const PhotoCard = React.memo(
  ({
    ratio,
    item,
  }: {
    ratio: string
    item: {
      id: string
      name: string
      url: string
      compressedUrl?: string
      b2Url?: string
      b2CompressedUrl?: string
    }
  }) => {
    const dispatch = useDispatch<MyDispatch>()
    const { category, data: eventData } = MySelector((state) => state.event)
    const {
      selectedPhotos,
      type,
      downloadData: { downloadEnabled },
      selectionMode
    } = MySelector((state) => state.util)
    const { albumMemberships } = MySelector((state) => state.gallery)
    const { theme } = MySelector((state) => state.theme)

    const filesInAnyAlbum = getFilesInAnyAlbum(albumMemberships)

    const { downloadFile, moveToPhysicalAlbum, shareFile } = HandleAsyncFns()

    const { url: optimalUrl, compressedUrl: optimalCompressedUrl } = getOptimalUrls(item)
    const isImageVisible = useLazyLoad(optimalUrl)

    const [isLoaded, setIsLoaded] = useState(false)

    const preventSelection = (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault()
      ;(event.currentTarget as HTMLElement).blur()
    }

    const handleSelect = () => {
      if (!isLoaded) return
      dispatch(setSelectedPhotos(item.id))
    }

    const handleFullScreen = () => {
      if (!isLoaded) return
      dispatch(setFullScreenObject(item))
      dispatch(setFullScreen(true))
    }

    return (
      <div
        id="photo-card"
        className={`relative ${ratio}`}
        onClick={selectionMode ? handleSelect : handleFullScreen}
        onMouseDown={preventSelection}
      >
        {!isLoaded ? (
          <div className="absolute top-0 left-0 w-full h-full bg-gray-700 animate-pulse" />
        ) : (
          <div
            className={`absolute top-0 left-0 right-0 bottom-0 opacity-0 bg-[#000000B2] z-10 ${
              selectedPhotos.includes(item.id) ? "opacity-100" : ""
            } ${selectionMode ? "lg:opacity-30 lg:hover:opacity-100" : "lg:hover:opacity-100"} transition-all duration-150 ease-linear`}
          >
            {selectionMode && (
                <div className="absolute bottom-[5px] lg:top-[5px] right-[5px]">
                    <Checkbox
                      sx={{
                        color: "white",
                        "&.Mui-checked": {
                          color: "#F6C67C",
                        },
                      }}
                      checked={selectedPhotos.includes(item.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
              </div>
            )}

            {category === "host" && !selectionMode && selectedPhotos?.length <= 1 && (
              <div
                className={`hidden lg:flex absolute bottom-[10px] right-[5px] z-50`}
              >
                {type === "gallery" && (
                  <Tooltip title="Move To Folder" arrow>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        dispatch(setMoveFolderArray?.([item.id]))
                      }}
                    >
                      <img
                        className={`cursor-pointer h-[25px] w-[25px]`}
                        src={moveIcon}
                        alt="move"
                      />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip title="Delete" arrow>
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation()
                      dispatch(setDeleteArray?.([item.id]))
                    }}
                  >
                    <img
                      className={`cursor-pointer h-[25px] w-[25px]`}
                      src={trashIcon}
                      alt="trash"
                    />
                  </IconButton>
                </Tooltip>

                {type === "gallery" && (
                  <Tooltip title="Add To Physical Album" arrow>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        moveToPhysicalAlbum?.([item.id])
                      }}
                    >
                      <img
                        className={`cursor-pointer h-[28px] w-[28px]`}
                        src={
                          checkInPhysicalAlbum(item.id, filesInAnyAlbum)
                            ? phyalbumGoldIcon
                            : phyalbumIcon
                        }
                        alt="add to physical"
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {downloadEnabled && type === "gallery" && (
                  <Tooltip title="Download" arrow>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        downloadFile?.([{ url: optimalUrl, name: item.name, compressedUrl: optimalCompressedUrl, id: item.id }])
                      }}
                    >
                      <img
                        className={`cursor-pointer h-[30px] w-[30px]`}
                        src={downloadIcon}
                        alt="download"
                      />
                    </IconButton>
                  </Tooltip>
                )}
                {type === "gallery" && (
                  <Tooltip title="Share" arrow>
                    <IconButton onClick={(e) => {
                      e.stopPropagation()
                      shareFile.setSharable(item.id)
                    }}>
                      <img
                        className={`cursor-pointer h-[20px] w-[20px]`}
                        src={shareIcon}
                        alt="share"
                      />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        )}
        {checkVideo(optimalUrl) && isLoaded && (
          <img
            src={theme === "dark" ? playIcon : playIconDark}
            alt=""
            className="absolute top-1/2 left-1/2 z-20 transform -translate-x-1/2 -translate-y-1/2"
          />
        )}

        {checkVideo(optimalUrl) ? (
          <video
            className={`w-full pointer-events-none h-full object-cover opacity-0 transition-opacity duration-300 ease-linear ${
              isLoaded ? "opacity-100" : ""
            }`}
            playsInline
            muted
            draggable="false"
            onLoadedMetadata={() => setIsLoaded(true)}
          >
            <source src={optimalCompressedUrl} />
          </video>
        ) : (
          <img
            id={optimalUrl}
            src={optimalCompressedUrl}
            alt={item.name}
            draggable="false"
            className={`w-full h-full object-cover opacity-0 transition-opacity duration-300 ease-linear ${
              isLoaded ? "opacity-100" : ""
            }`}
            loading={isImageVisible ? "lazy" : "eager"}
            onLoad={() => setIsLoaded(true)}
          />
        )}

        {/* Physical Album Marker (Host only) */}
        {category === "host" && type === "gallery" && isLoaded && checkInPhysicalAlbum(item.id, filesInAnyAlbum) && (
          <div className="absolute top-2 right-2">
            <img
              src={phyalbumGoldIcon}
              alt="In physical album"
              className="h-5 w-5 drop-shadow-lg"
            />
          </div>
        )}

        {/* Watermark Overlay */}
        {eventData?.watermarkEnabled && eventData?.watermarkText && isLoaded && (
          <WatermarkOverlay watermarkText={eventData.watermarkText} />
        )}
      </div>
    )
  }
)

export default PhotoCard
